/**
 * @file 家族の学びメモの一覧を読む hook (本だなの平積みとリーダーが使う)。
 *
 * @what: 家族の全件を読み、画面をまたいで覚えを使い回す (lib/sharedFetchCache.ts。API は最近さわった順)。
 *   期限内の覚えがあれば通信せず、期限を過ぎていれば覚えを出したまま取り直す。書いたり消したりした
 *   あとは api/notes.ts が覚えを捨てるので、次の面で取り直す。まなぶ人での絞り込みは呼び出し側
 *   (model/studyNoteContext.ts の notesForLearner)。
 * @why: メモ帳ペイン (StudyNotePane) は編集のために自前で読むので、読むだけの面はこの hook で足りる。
 *   本文込みの全件を本だなとリーダーが mount のたびに取り直していたので、覚えで1回にする。
 * @failure: 取得失敗は空の配列 (覚えがあればそれ) にして、利用者には知らせない。※なぜなら平積みは
 *   本だなの補助で、メモが読めなくても閲覧位置だけで組み立てられ、役割のセクション (本体) は
 *   影響を受けないためである。詳細は console に残す。
 */

import { useEffect, useState } from "react";
import { fetchStudyNotes, studyNotesCacheKey, type StudyNote } from "../api/notes";
import { loadShared, readShared } from "../lib/sharedFetchCache";

/** 覚えを取り直さずに使う期間である。書き込みのあとは期限に関わらず捨てる (api/notes.ts)。 */
export const STUDY_NOTES_TTL_MS = 5 * 60 * 1000;

const EMPTY: readonly StudyNote[] = [];

export function useStudyNotes(familyId: string): readonly StudyNote[] {
  const key = studyNotesCacheKey(familyId);
  const [notes, setNotes] = useState<readonly StudyNote[]>(
    () => readShared<StudyNote[]>(key, STUDY_NOTES_TTL_MS)?.value ?? EMPTY,
  );

  useEffect(() => {
    let cancelled = false;
    const hit = readShared<StudyNote[]>(key, STUDY_NOTES_TTL_MS);
    setNotes(hit ? hit.value : EMPTY);
    if (hit?.fresh) {
      return () => {
        cancelled = true;
      };
    }
    loadShared(key, () => fetchStudyNotes(familyId))
      .then((list) => {
        if (!cancelled) setNotes(list);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error("[study] failed to load notes", err);
      });
    return () => {
      cancelled = true;
    };
  }, [familyId, key]);

  return notes;
}
